import { useRoute, useRequestURL } from "#app";

import { BreadcrumbRoute, CATEGORY_MAP } from "~/types/breadcrumb-routes";

/**
 * Build a BreadcrumbList schema object from the current route.
 *
 * @param category  Route category enum value that determines the parent breadcrumb.
 * @param overrideName  Optional reactive reference for the final breadcrumb name
 *                      (e.g. `hero.value.heading` from content JSON).
 *
 * @returns Computed BreadcrumbList structured-data object ready for `innerHTML`.
 *
 * @example
 *   const route = useRoute();
 *   const url = useRequestURL();
 *   const { data } = await useFetch(url.origin + "/content/...");
 *
 *   const breadcrumbs = useBreadcrumbSchema(BreadcrumbRoute.Products, content.hero.heading);
 *
 *   useHead({
 *     script: [{
 *       type: "application/ld+json",
 *       innerHTML: breadcrumbs,
 *     }],
 *   });
 */
export function useBreadcrumbSchema(
  category: BreadcrumbRoute,
  overrideName?: string | import("vue").Ref<string>,
) {
  const route = useRoute();
  const url = useRequestURL();

  const baseUrl = url.origin;

  return computed(() => {
    // Split route path into segments, e.g. "/ai/capabilities/dataops"
    const segments = route.path.split("/").filter(Boolean);

    const items = [
      {
        "@type": "ListItem" as const,
        position: 1,
        name: "Home",
        item: baseUrl,
      },
    ];

    segments.forEach((slug: string, index: number) => {
      const isLast = index === segments.length - 1;

      // Determine the display name
      let name: string;
      if (isLast && overrideName !== undefined) {
        name = typeof overrideName === "string" ? overrideName : overrideName.value;
      } else {
        name =
          (CATEGORY_MAP as Record<string, string>)[slug] ??
          slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      }

      const path = "/" + segments.slice(0, index + 1).join("/");

      items.push({
        "@type": "ListItem" as const,
        position: index + 2,
        name,
        item: `${baseUrl}${path}`,
      });
    });

    return {
      "@type": "BreadcrumbList",
      itemListElement: items,
    };
  });
}